<template>
  <Popup :ref="(el) => popup = el"
         class="popup__lg"
         :title="$t('menu.items.accountOperations')"
         @onClose="hide"
  >
    <div class="filters">
      <form class="form" id="statistic-form">
        <div class="form-block form-block--25">
          <input type="text" class="form-control form-control-sm" :placeholder="helper.trans('labels.invoice_id')" v-model="requestDTO.dealId">
        </div>

        <div class="form-block form-block--25">
          <DatePicker v-model="requestDTO.fromDate" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
            <template v-slot="{ inputValue, togglePopover, inputEvents }">
              <input
                class="form-control form-control-sm"
                :placeholder="helper.trans('labels.from_date')"
                :value="inputValue"
                @focus="togglePopover"
                @change="inputEvents.change"
              />
            </template>
          </DatePicker>
        </div>

        <div class="form-block form-block--25">
          <DatePicker v-model="requestDTO.toDate" mode="dateTime" :model-modifiers="{string: true}"  is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
            <template v-slot="{ inputValue, togglePopover, inputEvents }">
              <input
                class="form-control form-control-sm"
                :placeholder="helper.trans('labels.to_date')"
                :value="inputValue"
                @focus="togglePopover"
                @change="inputEvents.change"
              />
            </template>
          </DatePicker>
        </div>

        <div class="form-block form-block--25">
          <Multiselect id="types"
                       class="multiselect-sm"
                       v-model="requestDTO.types"
                       :options="Object.values(OperationType)"
                       :custom-label="(opt) => helper.trans(`operation_type.${opt}`)"
                       :multiple="true"
                       select-label="Select"
                       :placeholder="helper.trans('labels.type')"
          >
          </Multiselect>
        </div>

        <div class="form__controls">
          <LoaderButton :on-click="applyFilters" class="form__btn btn">
            <span class="text-capitalize-first">{{$t('button.apply')}}</span>
          </LoaderButton>
          <LoaderButton :on-click="clearFilters" class="form__btn form__btn--reset btn btn--2">
            <span class="text-capitalize-first">{{$t('button.reset')}}</span>
          </LoaderButton>
        </div>
      </form>
    </div>
    <LoaderContent ref="loader" class="">
      <div class="popup__content">
        <div style="overflow: auto">
          <table class="table table-striped" style="position: relative">
            <thead>
            <tr>
              <th class="text-start p-sm">{{ helper.trans('labels.date')}}</th>
              <th class="text-start p-sm">{{ helper.trans('labels.currency')}}</th>
              <th class="text-start p-sm">{{ helper.trans('labels.sum')}}</th>
              <th class="text-start p-sm">{{ helper.trans('labels.type')}}</th>
              <th class="text-start p-sm">{{ helper.trans('labels.balance_before')}}</th>
              <th class="text-start p-sm">{{ helper.trans('labels.balance_after')}}</th>
              <th class="text-start p-sm"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="operation in operations.items" :key="operation.id">
              <td class="p ps">{{ dateHelper.format(operation.createdAt) }}</td>
              <td class="p">{{ operation.currency }}</td>
              <td class="p">{{ operation.transactionType === TransactionType.CREDIT ? "+" : '-' }}{{ numberFormatter.formatNumber(operation.sum)}}</td>
              <td class="p">{{ $t(`operation_type.${operation.operationType}`) }}</td>
              <td class="p">{{ numberFormatter.formatNumber(operation.balanceBefore)}}</td>
              <td class="p">{{ numberFormatter.formatNumber(operation.balanceAfter)}}</td>
              <td class="text-end p">
                <button style="display: none" type="button" @click="" class="btn btn-sm btn-outline-dark text-nowrap text-capitalize">
                  <SvgIcon icon="details"/>
                </button>
              </td>
            </tr>
            </tbody>
          </table>

          <b-pagination
            v-if="operations.totalCount > operations.items.length"
            v-model="page"
            :total-rows="operations.totalCount"
            :per-page="operations.limit"
            :limit="20"
            @update:modelValue="load()"
            prev-text="Back"
            next-text="Next"
            align="start"
            first-number
            hide-goto-end-buttons
            hide-ellipsis
          ></b-pagination>
        </div>
      </div>

    </LoaderContent>
  </Popup>
</template>
<script lang="ts">
import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from '@vue/reactivity';
import Popup from "@/apps/public/components/Popup.vue";
import {$t, helper} from "@/service/CommonHelper";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import LoaderButton from "@/apps/public/components/LoaderButton.vue";
import { useStore } from 'vuex';
import { api } from '@/service/APIClient';
import LoaderContent from "@/apps/public/components/LoaderContent.vue";
import {BPagination} from "bootstrap-vue-3";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import {dateHelper} from "@/service/DateHelper";
import {DisputeReason} from "@/models/enum/DisputeReason";
import {numberFormatter} from "@/service/NumberFormatter";
import CopyButton from "@/apps/public/components/CopyButton.vue";
import {Direction} from "@/models/enum/Direction";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import SvgIcon from "@/apps/public/components/SvgIcon.vue";
import moment from "moment";
import AccountOperationsGetRequestDTO from "@/models/request/AccountOperationsGetRequestDTO";
import AccountOperationDTO from "@/models/AccountOperationDTO";
import {TransactionType} from "@/models/enum/TransactionType";
import {DatePicker} from "v-calendar";
import Multiselect from "vue-multiselect";
import {OperationType} from "@/models/enum/OperationType";
import AccountDTO from "@/models/AccountDTO";


const defaultRequestDTO = {
  currency: null,
  dealId: '',
  storeIds: [],
  types: [],
  fromDate: moment().subtract(7, 'day').format('YYYY-MM-DD 00:00:00'),
  toDate: null,
  ids: []
} as AccountOperationsGetRequestDTO;

export default defineComponent({
  methods: {$t},
  components: {
    DatePicker,
    Multiselect,
    SvgIcon, PayMethodIcon, CopyButton, BPagination, LoaderContent, LoaderButton, FormInputWrapper, Popup},
  setup() {
    const popup = ref<InstanceType<typeof Popup> | null>(null)
    const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
    const store = useStore()
    const state = reactive({
      account: null as AccountDTO|null,
      requestDTO: {
        ...defaultRequestDTO
      } as AccountOperationsGetRequestDTO,
      page: 1,
      operations: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<AccountOperationDTO>
    });


    const load = async () => {
      loader.value?.load(_load)
    }
    const _load = async () => {
      state.operations.offset = state.operations.limit * (state.page - 1)
      if (state.account) {
        state.requestDTO.ids = [state.account!.id]
      }
      state.operations = await api.accountOperations(state.operations, {createdAt: 'desc'}, {
        ...state.requestDTO,
        fromDate: dateHelper.toUTC(state.requestDTO.fromDate),
        toDate: dateHelper.toUTC(state.requestDTO.toDate)
      })
    }

    const show = async (account: AccountDTO|null = null) => {
      state.account = account
      popup.value?.show()
      load()
    }

    const hide = () => {
      popup.value?.hide()
    }

    const applyFilters = async () => {
      state.page = 1;
      await load()
    }

    const clearFilters = async () => {
      state.page = 1;
      state.requestDTO = {...defaultRequestDTO}
      await load()
    }

    return {
      ...toRefs(state),
      loader,
      popup,
      helper,
      clearFilters,
      applyFilters,
      show,
      dateHelper,
      DisputeReason,
      load,
      numberFormatter,
      api,
      TransactionType,
      OperationType,
      Direction,
      hide,
    }
  }
})
</script>
